import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  GenericAbortSignal,
  ResponseType,
} from 'axios'
import humps from 'humps'

const withCredentials = Boolean(import.meta.env.DEV) // axiosの設定: DEV環境の場合はCORS対策のためwithCredentialsをtrueにする

const createAxiosInstance = (responseType: ResponseType): AxiosInstance => {
  const instance = axios.create({
    baseURL: location.origin,
    timeout: 60000 * 5, // 5m
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    withCredentials,
    responseType,
  })

  // リクエストデータをスネークケースに変換する
  instance.interceptors.request.use((request) => {
    if (request.data) {
      request.data = humps.decamelizeKeys(request.data)
    }
    if (request.params) {
      request.params = humps.decamelizeKeys(request.params)
    }
    return request
  })

  // レスポンスデータをキャメルケースに変換する
  instance.interceptors.response.use(
    (response) => {
      const currentResponseType = response.config?.responseType ?? responseType
      if (currentResponseType === 'json') {
        response.data = humps.camelizeKeys(response.data)
      }
      return response
    },
    (error) => {
      if (axios.isCancel(error)) {
        // リクエストがキャンセルされた場合のエラーは何もしない
        return null
      }

      if (axios.isAxiosError(error)) {
        const res = error.response
        const currentResponseType = error.config?.responseType ?? responseType
        if (currentResponseType === 'json' && res?.data) {
          res.data = humps.camelizeKeys(res.data)
        }
      }

      return Promise.reject(error)
    },
  )

  return instance
}

const axiosClient: AxiosInstance = createAxiosInstance('json')

export type QueryResponse<T> = AxiosResponse<T>

export type QueryResult<T> = QueryResponse<T>

export type MutateResponse<T> = AxiosResponse<T>

export type ErrorResponse<T = unknown> = AxiosError<T>

export const GET = async <TRes, TParams = {}>(
  url: string,
  params?: TParams,
  signal?: GenericAbortSignal,
  responseType?: ResponseType,
): Promise<AxiosResponse<TRes>> =>
  axiosClient
    .get(url, { params, signal, responseType })
    .then((res) => {
      //todo: ver.1.2.0以降でダウンロード処理切り分ける
      if (responseType === 'blob') {
        const blobData = new Blob([res.data], {
          type: res.headers['content-type'],
        })
        const downloadUrl = window.URL.createObjectURL(blobData)
        const link = document.createElement('a')
        link.href = downloadUrl
        const cd = res.headers['content-disposition']
        let filename = ''
        if (cd) {
          const utf8Match = cd?.match(/filename\*=utf-8''([^;\s]+)/i)
          if (utf8Match) {
            filename = decodeURIComponent(utf8Match[1])
          } else {
            filename = cd.match(/filename="?([^";\s]+)"?/i)?.[1]
          }
        }
        link.download =
          filename ?? 'download_' + new Date().toLocaleString('ja-JP')
        link.click()

        // メモリのクリーンアップ
        setTimeout(() => {
          window.URL.revokeObjectURL(downloadUrl)
        }, 100)
      }
      return res
    })
    .catch((err) => {
      return Promise.reject(catchUnauthorized(err))
    })

export const POST = async <TRes, TParams = {}>(
  url: string,
  params?: TParams,
): Promise<AxiosResponse<TRes>> =>
  axiosClient
    .post(url, params)
    .then((res: AxiosResponse<TRes>) => res)
    .catch((err) => {
      return Promise.reject(catchUnauthorized(err))
    })

export const PUT = async <TRes, TParams = {}>(
  url: string,
  params?: TParams,
): Promise<AxiosResponse<TRes>> =>
  axiosClient
    .put(url, params)
    .then((res) => res)
    .catch((err) => {
      return Promise.reject(catchUnauthorized(err))
    })

export const DELETE = async <TRes, TParams = {}>(
  url: string,
  params?: TParams,
): Promise<AxiosResponse<TRes>> =>
  axiosClient
    .delete(url, { params })
    .then((res) => res)
    .catch((err) => {
      return Promise.reject(catchUnauthorized(err))
    })

function catchUnauthorized(err: AxiosError): AxiosError | null {
  // 以下のエラーの場合はログイン画面へリダイレクトする
  // 401: 認証エラー
  // 419: CSRFトークンの不一致
  if (err.status === 401 || err.status === 419) {
    window.location.href = '/auth/login?session_timeout=true'
    return null
  }
  return err
}
